"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = buildMentalRotationCatTimeline;
require("regenerator-runtime/runtime");
// setup
const taskSetup_1 = require("../taskSetup");
const helpers_1 = require("../shared/helpers");
// trials
const instructions_1 = require("./trials/instructions");
const trials_1 = require("../shared/trials");
const config_1 = require("./helpers/config");
const prepareCat_1 = require("../shared/helpers/prepareCat");
const taskStore_1 = require("../../taskStore");
const batchPreloading_1 = require("../shared/helpers/batchPreloading");
const downexInstructions_1 = require("./trials/downexInstructions");
function buildMentalRotationCatTimeline(config, mediaAssets) {
    const { heavyInstructions } = (0, taskStore_1.taskStore)();
    const { semThreshold } = (0, taskStore_1.taskStore)();
    (0, helpers_1.initTrialSaving)(config);
    const initialTimeline = (0, helpers_1.initTimeline)(config, trials_1.enterFullscreen);
    const ifRealTrialResponse = {
        timeline: [(0, trials_1.getAudioResponse)(mediaAssets)],
    };
    let corpus = (0, taskStore_1.taskStore)().corpora.stimulus;
    const translations = (0, taskStore_1.taskStore)().translations;
    const validationErrorMap = {};
    const layoutConfigMap = {};
    for (const c of corpus) {
        const { itemConfig, errorMessages } = (0, config_1.getLayoutConfig)(c, translations, mediaAssets);
        layoutConfigMap[c.itemId] = itemConfig;
        if (errorMessages.length) {
            validationErrorMap[c.itemId] = errorMessages.join('; ');
        }
    }
    if (Object.keys(validationErrorMap).length) {
        console.error('The following errors were found');
        console.table(validationErrorMap);
        throw new Error('Something went wrong. Please look in the console for error details');
    }
    const corpora = (0, prepareCat_1.prepareCorpus)(corpus);
    // organize media assets into batches for preloading
    const batchedCorpus = (0, helpers_1.prepareMultiBlockCat)(corpora.cat);
    const batchedMediaAssets = (0, helpers_1.batchMediaAssets)(mediaAssets, batchedCorpus, ['item', 'answer', 'distractors']);
    (0, taskStore_1.taskStore)('corpora', {
        practice: (0, taskStore_1.taskStore)().corpora.practice,
        stimulus: batchedCorpus,
    });
    // counter for next batch to preload (skipping the initial preload)
    let currPreloadBatch = 0;
    const initialMedia = (0, batchPreloading_1.getLeftoverAssets)(batchedMediaAssets, mediaAssets);
    const initialPreload = (0, helpers_1.createPreloadTrials)(initialMedia).default;
    const instructions = heavyInstructions
        ? downexInstructions_1.downexInstructions
        : [instructions_1.imageInstructions, instructions_1.videoInstructionsMisfit, instructions_1.videoInstructionsFit];
    const timeline = [initialPreload, initialTimeline, ...instructions];
    const trialConfig = {
        trialType: 'audio',
        responseAllowed: true,
        promptAboveButtons: true,
        task: config.task,
        layoutConfig: {
            showPrompt: true,
        },
        layoutConfigMap,
        terminateCat: true, // if running cat, stop if 4 of last 10 trials have been incorrect
    };
    const stimulusBlock = (index) => {
        return {
            timeline: [
                { ...trials_1.setupStimulusFromCurrentCatBlock, stimulus: '' },
                (0, trials_1.afcStimulusTemplate)(trialConfig),
                ifRealTrialResponse,
            ],
            conditional_function: () => {
                if ((0, taskStore_1.taskStore)().skipBlock === index) {
                    return false;
                }
                return true;
            },
        };
    };
    const polygonInstructBlock = {
        timeline: [instructions_1.polygonInstructions],
        conditional_function: () => {
            return (0, taskStore_1.taskStore)().currentCatBlock === 1 && (0, helpers_1.isEnglish)((0, taskStore_1.taskStore)().language);
        },
    };
    const threeDimInstructBlock = {
        timeline: [instructions_1.threeDimInstructions],
        conditional_function: () => {
            return (0, taskStore_1.taskStore)().currentCatBlock === 2;
        },
    };
    function preloadBatch() {
        timeline.push((0, helpers_1.createPreloadTrials)(batchedMediaAssets[currPreloadBatch]).default);
        currPreloadBatch++;
    }
    const instructionPractice = corpora.ipLight;
    const presentedInstructions = [];
    // returns practice + instruction trials for a given block
    function getPracticeInstructions(blockNum) {
        const trials = instructionPractice.filter((trial) => trial.block_index === blockNum && !presentedInstructions.includes(trial.block_index));
        return trials;
    }
    const instructionPracticeBlock = (blockNum) => {
        const trials = getPracticeInstructions(blockNum);
        return {
            timeline: [
                polygonInstructBlock,
                threeDimInstructBlock,
                ...trials.map((trial) => {
                    return {
                        timeline: [{ ...trials_1.fixationOnly, stimulus: '' }, (0, trials_1.afcStimulusTemplate)(trialConfig, trial)],
                    };
                }),
            ],
            conditional_function: () => {
                const run = (0, taskStore_1.taskStore)().currentCatBlock === blockNum - 1 && !presentedInstructions.includes(blockNum);
                if (run) {
                    presentedInstructions.push(blockNum);
                }
                return run;
            },
        };
    };
    const firstBlockPractice = corpus.filter((trial) => Number(trial.block_index) === 1 && trial.assessmentStage === 'practice_response');
    let fellBack = false;
    const fallbackInstructions = {
        timeline: [
            trials_1.repeatInstructionsMessage,
            ...downexInstructions_1.downexInstructions,
            ...firstBlockPractice.map((trial) => (0, trials_1.afcStimulusTemplate)(trialConfig, trial)),
        ],
        conditional_function: () => {
            const run = (0, helpers_1.checkFallbackCriteria)() && !fellBack;
            if (run) {
                fellBack = true;
            }
            return run;
        },
    };
    function addInstructionPractice() {
        batchedCorpus.forEach((block, index) => {
            timeline.push(instructionPracticeBlock(index + 1));
        });
    }
    (0, taskStore_1.taskStore)('currentCatBlock', 0);
    const numOfCatTrials = corpora.cat.length;
    (0, taskStore_1.taskStore)('totalTestTrials', numOfCatTrials);
    batchedCorpus.forEach((block, index) => {
        preloadBatch();
        // add in instructions for all blocks each time: only the correct one will run based on currentCatBlock in taskStore
        addInstructionPractice();
        if (index === 0) {
            timeline.push((0, trials_1.practiceTransition)(heavyInstructions ? () => 'mentalRotationInstruct5Downex' : undefined));
            // push in starting block
            corpora.start.forEach((trial) => {
                timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
                timeline.push((0, trials_1.afcStimulusTemplate)(trialConfig, trial));
                timeline.push(ifRealTrialResponse);
            });
        }
        else {
            timeline.push((0, trials_1.practiceTransition)(() => 'generalYourTurn'));
        }
        const numOfTrials = block.length / 3;
        const fallBackIndex = 4;
        for (let i = 0; i < numOfTrials; i++) {
            if (i <= fallBackIndex && index === 0) {
                timeline.push(fallbackInstructions);
            }
            timeline.push(stimulusBlock(index));
        }
        // check the participant's theta and assign next block
        if (index < batchedCorpus.length - 1) {
            timeline.push(trials_1.setupNextBlock);
        }
    });
    (0, taskSetup_1.initializeCat)();
    timeline.push((0, trials_1.taskFinished)());
    timeline.push(trials_1.exitFullscreen);
    return { jsPsych: taskSetup_1.jsPsych, timeline };
}
